import React from 'react';
import { DndContext, closestCorners, useDroppable, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove } from '@dnd-kit/sortable';
import RecipeCard from './RecipeCard';
import { recipeAPI, listAPI } from '../services/api';

// Droppable List Component
function DroppableList({ list, children }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `list-${list.id}`,
  });

  return (
    <div 
      ref={setNodeRef}
      className="board-list"
      style={{
        backgroundColor: isOver ? 'var(--light-blue)' : 'var(--white)',
        transition: 'background-color 0.2s',
        border: isOver ? '2px dashed var(--primary-blue)' : 'none'
      }}
    >
      {children}
    </div>
  );
}

// Droppable Recipe Cards Container
function DroppableCardsContainer({ listId, children }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `cards-${listId}`,
  });

  return (
    <div 
      ref={setNodeRef}
      className="recipe-cards"
      style={{
        minHeight: isOver ? '100px' : 'auto',
        backgroundColor: isOver ? 'rgba(74, 144, 226, 0.05)' : 'transparent',
        borderRadius: isOver ? '8px' : '0',
        transition: 'all 0.2s'
      }}
    >
      {children}
    </div>
  );
}

function Board({ board, onAddRecipe, onRecipeClick, onUpdate }) {
  // Configure sensors for both mouse and touch devices (tablets/mobile)
  // Must be called before any early returns (React Hooks rule)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before starting drag
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200, // 200ms delay on touch to distinguish from tap
        tolerance: 5, // Allow 5px of movement during delay
      },
    })
  );

  if (!board) return <div>No board selected</div>;

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    try {
      // Find source list (where the recipe is currently)
      const sourceList = board.lists.find(list =>
        list.recipes.some(r => r.id === active.id)
      );

      if (!sourceList) return;

      // Find destination list
      let destList = null;
      let overRecipeId = null;
      
      // Check if dropped on a list drop zone
      const overIdStr = over.id.toString();
      if (overIdStr.startsWith('list-')) {
        const listId = parseInt(overIdStr.replace('list-', ''));
        destList = board.lists.find(list => list.id === listId);
      } else if (overIdStr.startsWith('cards-')) {
        // Dropped on the cards container
        const listId = parseInt(overIdStr.replace('cards-', ''));
        destList = board.lists.find(list => list.id === listId);
      } else {
        // Dropped on another recipe card - find which list contains that recipe
        destList = board.lists.find(list =>
          list.recipes.some(r => r.id === over.id)
        );
        if (destList) {
          overRecipeId = over.id;
        }
      }

      // If still no destination, try to find list by matching over.id directly
      if (!destList) {
        destList = board.lists.find(list => list.id === over.id);
      }

      if (!destList) return;

      // If moving within the same list, reorder
      if (destList.id === sourceList.id) {
        const recipeIds = sourceList.recipes.map(r => r.id);
        const oldIndex = recipeIds.indexOf(active.id);
        const newIndex = overRecipeId 
          ? recipeIds.indexOf(overRecipeId)
          : recipeIds.length - 1;
        
        // Only update if position actually changed
        if (oldIndex !== newIndex) {
          const reorderedIds = arrayMove(recipeIds, oldIndex, newIndex);
          
          // Update positions for all recipes in the list
          const updatePromises = reorderedIds.map((recipeId, index) => {
            return recipeAPI.update(recipeId, { position: index });
          });
          
          await Promise.all(updatePromises);
          await onUpdate();
        }
      } else {
        // Moving to a different list
        await recipeAPI.update(active.id, { list_id: destList.id });
        await onUpdate();
      }
    } catch (error) {
      console.error('Failed to move recipe:', error);
    }
  };

  const handleDeleteList = async (listId) => {
    if (!window.confirm('Delete this list and all its recipes?')) return;
    
    try {
      await listAPI.delete(listId);
      await onUpdate();
    } catch (error) {
      console.error('Failed to delete list:', error);
    }
  };

  return (
    <DndContext onDragEnd={handleDragEnd} collisionDetection={closestCorners} sensors={sensors}>
      <div className="board-view">
        {board.lists.map(list => (
          <DroppableList key={list.id} list={list}>
            <div className="list-header">
              <h3 className="list-title">{list.title}</h3>
              <div className="list-actions">
                <button
                  className="icon-button"
                  onClick={() => onAddRecipe(list.id)}
                  title="Add recipe"
                >
                  +
                </button>
                <button
                  className="icon-button"
                  onClick={() => handleDeleteList(list.id)}
                  title="Delete list"
                >
                  ×
                </button>
              </div>
            </div>

            <DroppableCardsContainer listId={list.id}>
              <SortableContext items={list.recipes.map(r => r.id)}>
                {list.recipes.map(recipe => (
                  <RecipeCard
                    key={recipe.id}
                    recipe={recipe}
                    onClick={() => onRecipeClick(recipe)}
                    onUpdate={onUpdate}
                  />
                ))}
              </SortableContext>
            </DroppableCardsContainer>
          </DroppableList>
        ))}

        <div className="add-list-button" onClick={() => {
          const title = prompt('List name:');
          if (title) {
            listAPI.create(board.id, title).then(onUpdate);
          }
        }}>
          + Add List
        </div>
      </div>
    </DndContext>
  );
}

export default Board;
